/**
 * The Backlog reading pane's width (epic PI-SPEC-SW-E289, feature
 * PI-SPEC-SW-1473; UX-PM-18 and UX-PM-30 in
 * docs/analysis/toolbox-ux/03-evaluation.md, Claude Design frame 1b).
 *
 * The pane opens at 45% of the split, never below 540 px (frame 1b at a
 * 1280 px window), so a wide window gives the brief more room. It is resized
 * from its left edge, and the width is remembered per computer (localStorage,
 * the same pattern as backlog-sort.ts). The Toolbox window is at least 1180 x 740 with a
 * 76 px rail, so the width is clamped to keep the list at least 360 px wide and
 * the pane at least 380 px.
 */

/** localStorage key of the remembered pane width. */
export const PANE_WIDTH_STORAGE_KEY = "spectree.grooming.readingPaneWidth";

/** The narrowest the pane opens when nothing (or something invalid) is remembered (frame 1b). */
export const PANE_WIDTH_DEFAULT = 540;

/** The share of the split the pane opens at when nothing is remembered. */
export const PANE_WIDTH_SHARE = 0.45;

/** The narrowest the pane gets. */
export const PANE_WIDTH_MIN = 380;

/** The narrowest the list beside the pane gets. */
export const LIST_MIN_WIDTH = 360;

/** One arrow-key press on the pane's resize handle. */
export const PANE_WIDTH_STEP = 24;

/** True for a width worth keeping: a finite number of pixels at least the minimum. */
function isUsableWidth(value: number): boolean {
  return Number.isFinite(value) && value >= PANE_WIDTH_MIN;
}

/** The remembered width, or null when none is stored, it is invalid, or storage is unavailable. */
export function readPaneWidth(): number | null {
  try {
    const stored = window.localStorage.getItem(PANE_WIDTH_STORAGE_KEY);
    if (stored === null || !/^\d+(\.\d+)?$/.test(stored.trim())) return null;
    const width = Math.round(Number(stored));
    return isUsableWidth(width) ? width : null;
  } catch {
    return null;
  }
}

/**
 * The width the pane opens at when none is remembered: PANE_WIDTH_SHARE of a
 * split of `containerWidth` pixels, at least PANE_WIDTH_DEFAULT. An unknown
 * container width (0, not measured yet) gives PANE_WIDTH_DEFAULT. Pure.
 */
export function defaultPaneWidth(containerWidth: number): number {
  return Math.max(PANE_WIDTH_DEFAULT, Math.round(containerWidth * PANE_WIDTH_SHARE));
}

/** Remember the width; a storage failure (private mode, quota) only means it is not remembered. */
export function writePaneWidth(px: number): void {
  if (!isUsableWidth(px)) return;
  try {
    window.localStorage.setItem(PANE_WIDTH_STORAGE_KEY, String(Math.round(px)));
  } catch {
    // Not remembered; the pane keeps the width for this visit.
  }
}

/**
 * The width the pane may have inside a split container of `containerWidth`
 * pixels: at least PANE_WIDTH_MIN, and at most what leaves the list
 * LIST_MIN_WIDTH. When the container is too narrow for both, the pane keeps
 * its minimum. An unknown container width (0, not measured yet) only applies
 * the minimum. Pure.
 */
export function clampPaneWidth(px: number, containerWidth: number): number {
  const width = Number.isFinite(px) ? Math.round(px) : PANE_WIDTH_DEFAULT;
  const max =
    containerWidth > 0
      ? Math.max(PANE_WIDTH_MIN, Math.floor(containerWidth - LIST_MIN_WIDTH))
      : Number.POSITIVE_INFINITY;
  return Math.min(Math.max(width, PANE_WIDTH_MIN), max);
}
